import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Show, useClerk } from "@clerk/react";
import { toast } from "sonner";
import { useT, useSeo, fmtMoney, fmtDateTime, userTz } from "@/lib/i18n";
import { CountrySelect, PhoneField, phoneValid, useX } from "@/components/extras";
import { useApi, errMsg, API } from "@/lib/api";
import { PageHead, Notice, CtaLink } from "@/components/bits";
import avatars from "@/avatars-manifest.json";
import content from "@/interactive-content.json";

const TABS = ["overview", "bookings", "orders", "club", "saved", "profile", "notifications", "privacy"];

function Bookings({ list, reload }) {
  const { t, tl, pick, locale, lp } = useT();
  const api = useApi();
  if (!list.length) return <p data-testid="bookings-empty">{t("emptyBookings")}</p>;
  const ics = async (b) => { const r = await api.get(`/me/bookings/${b.id}/ics`, { responseType: "blob" }); const u = URL.createObjectURL(r); const a = document.createElement("a"); a.href = u; a.download = "session.ics"; a.click(); };
  const cancel = async (b) => { if (!window.confirm(t("policyAck"))) return; try { await api.post(`/me/bookings/${b.id}/cancel`); reload(); } catch (e) { toast.error(errMsg(e, tl("errors")[5])); } };
  return list.map((b) => <BookingRow key={b.id} b={b} ics={ics} cancel={cancel} reload={reload} />);
}

function Reschedule({ b, reload, onDone }) {
  const { t, tl, locale } = useT();
  const api = useApi();
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState(null);
  useEffect(() => { if (date) api.get(`/public/availability?service=${b.service_id}&date=${date}&timezone=${encodeURIComponent(b.timezone_snapshot)}`).then((r) => setSlots(r.slots)).catch(() => setSlots([])); }, [date, b, api]);
  const go = async (s) => { if (!window.confirm(`${t("reschedule")}: ${fmtDateTime(s, b.timezone_snapshot, locale)}?`)) return; try { await api.post(`/me/bookings/${b.id}/reschedule`, { start_utc: s }); toast.success(tl("controls")[0]); onDone(); reload(); } catch (e) { toast.error(errMsg(e, tl("errors")[5])); } };
  return (
    <div className="grid gap-2" data-testid="reschedule-box">
      <input type="date" min={new Date(Date.now() + 864e5).toISOString().slice(0, 10)} className="border rounded-xl px-3 py-2 max-w-[220px]" value={date} onChange={(e) => setDate(e.target.value)} aria-label={tl("fields")[3]} data-testid="reschedule-date" />
      {slots && (slots.length ? <div className="flex flex-wrap gap-2">{slots.map((s) => <button key={s} className="chip" onClick={() => go(s)} data-testid="reschedule-slot">{fmtDateTime(s, b.timezone_snapshot, locale, { timeStyle: "short" })}</button>)}</div> : <p className="help">{t("noSlots")}</p>)}
    </div>
  );
}

function BookingRow({ b, ics, cancel, reload }) {
  const { t, tl, pick, locale, lp } = useT();
  const [open, setOpen] = useState(false);
  const canResched = b.state === "confirmed" && new Date(b.start_utc) - Date.now() >= 72 * 3600e3;
  return (
    <div className="card p-5 grid gap-2" data-testid="booking-row">
      <div className="flex justify-between flex-wrap gap-2"><strong className="serif text-2xl">{pick(b.service_title)} · {b.duration_min} {t("minutes")}</strong><span className="chip text-xs" data-testid="booking-state">{b.state}</span></div>
      <p className="text-sm">{fmtDateTime(b.start_utc, b.timezone_snapshot, locale)} ({b.timezone_snapshot}) · {b.language.toUpperCase()} · {fmtMoney(b.price_snapshot.net_minor, b.currency, locale)}</p>
      <p className="text-xs text-[var(--muted)]">Ref {b.id.slice(-8).toUpperCase()}</p>
      <div className="flex flex-wrap gap-2">
        {b.state === "confirmed" && <><Link to={lp(`/session/${b.id}`)} className="btn btn-gold btn-sm">{tl("bookStates")[7]}</Link><button className="btn btn-ghost btn-sm" onClick={() => ics(b)}>.ics</button></>}
        {["confirmed", "held", "payment_pending"].includes(b.state) && <button className="btn btn-ghost btn-sm" onClick={() => cancel(b)} data-testid="booking-cancel">{tl("controls")[2]}</button>}
        {canResched && <button className="btn btn-ghost btn-sm" onClick={() => setOpen(!open)} data-testid="booking-reschedule">{t("reschedule")}</button>}
      </div>
      {open && <Reschedule b={b} reload={reload} onDone={() => setOpen(false)} />}
    </div>
  );
}

function ClubTab({ club }) {
  const { t, tl } = useT();
  if (!club) return null;
  const P = tl("progress");
  const name = { cooper: "Aya Cooper", gold: "Aya Gold", soul: "Aya Soul" };
  const pct = club.next_tier_points ? Math.min(100, (club.points / club.next_tier_points) * 100) : 100;
  return (
    <div className="grid gap-4" data-testid="club-tab">
      <div className="card p-6"><p className="eyebrow">{t("proposedTag")}</p><p className="serif text-4xl" data-testid="club-tier">{name[club.tier]}</p>
        <p className="mt-2" data-testid="club-points">{club.next_tier ? P[0].replace("{current}", club.points).replace("{next}", club.next_tier_points).replace("{tier}", name[club.next_tier]) : P[2]}</p>
        <div className="h-2 bg-[var(--line)] rounded mt-3" role="progressbar" aria-valuenow={club.points} aria-valuemin={0} aria-valuemax={club.next_tier_points || club.points}><div className="h-2 bg-[var(--gold)] rounded" style={{ width: `${pct}%` }} /></div>
        {club.next_tier && <p className="help mt-2">{P[1].replace("{remaining}", club.points_remaining)}</p>}</div>
      <p className="help">{t("pointsNote")}</p>
      {club.history.map((h) => <p key={h.id} className="text-sm">{h.created_at?.slice(0, 10)} · {h.source} · {h.points_delta > 0 ? "+" : ""}{h.points_delta} ({h.reason})</p>)}
    </div>
  );
}

function Profile({ me, reload }) {
  const { t, tl, pick, lp } = useT();
  const x = useX();
  const api = useApi();
  const F = tl("profileFields");
  const [v, setV] = useState({ nickname: me.nickname || "", avatar_id: me.avatar_id, country: me.country || "", locale: me.locale || "en", timezone: me.timezone || userTz(), phone: me.phone || "", terms: !!me.terms_accepted_at });
  const [error, setError] = useState("");
  const save = async (e) => {
    e.preventDefault(); setError("");
    if (!phoneValid(v.phone)) { setError(x("phoneBad")); return; }
    try { await api.patch("/me/profile", { nickname: v.nickname, avatar_id: v.avatar_id || undefined, country: v.country.toUpperCase() || undefined, locale: v.locale, timezone: v.timezone, phone: v.phone || undefined, terms_accepted: v.terms || undefined, privacy_accepted: v.gdpr || undefined }); toast.success(tl("controls")[0]); reload(); }
    catch (er) { setError(er?.response?.data?.code === "nickname_taken" ? tl("errors")[2] : errMsg(er, tl("errors")[5])); }
  };
  const upload = async (file) => {
    if (!file) return; const fd = new FormData(); fd.append("file", file);
    try { await api.post("/me/avatar-upload", fd); setV((x) => ({ ...x, avatar_id: "upload" })); setPhotoKey(Date.now()); toast.success(tl("controls")[0]); } catch (er) { toast.error(errMsg(er, tl("errors")[5])); }
  };
  const [photoKey, setPhotoKey] = useState(me.has_upload ? 1 : 0);
  const [photo, setPhoto] = useState(null);
  useEffect(() => {
    if (!photoKey) return undefined;
    let url;
    api.get(`/avatars/${me.user_id}`, { responseType: "blob" }).then((b) => { url = URL.createObjectURL(b); setPhoto(url); }).catch(() => setPhoto(null));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [photoKey, api, me.user_id]);
  return (
    <form onSubmit={save} className="grid gap-5" data-testid="profile-form">
      {!me.nickname && <Notice>{t("welcomeNick")}</Notice>}
      <label className="field">{F[0]}<input required minLength={3} maxLength={24} value={v.nickname} onChange={(e) => setV({ ...v, nickname: e.target.value })} data-testid="profile-nickname" /><span className="help">{t("nickHint")}</span></label>
      <fieldset><legend className="text-sm font-semibold">{F[1]}</legend><p className="help mb-2">{t("avatarHint")}</p>
        <div className="grid grid-cols-5 sm:grid-cols-10 gap-2" data-testid="avatar-grid">
          {photo && <button type="button" aria-pressed={v.avatar_id === "upload"} aria-label={F[2]} title={F[2]} onClick={() => setV({ ...v, avatar_id: "upload" })} className={`rounded-lg overflow-hidden border-2 ${v.avatar_id === "upload" ? "border-[var(--gold)]" : "border-transparent"}`} data-testid="avatar-uploaded"><img src={photo} alt="" className="w-full aspect-[3/4] object-cover" /></button>}
          {avatars.avatars.map((a) => <button type="button" key={a.id} aria-pressed={v.avatar_id === a.id} aria-label={pick(a.name)} title={pick(a.name)} onClick={() => setV({ ...v, avatar_id: a.id })} className={`rounded-lg overflow-hidden border-2 ${v.avatar_id === a.id ? "border-[var(--gold)]" : "border-transparent"}`} data-testid={`avatar-${a.id}`}><img src={`/${a.path}`} alt={pick(a.alt)} loading="lazy" className="w-full aspect-[3/4] object-cover" /></button>)}
        </div></fieldset>
      <label className="field">{F[2]}<input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => upload(e.target.files[0])} data-testid="avatar-upload" /><span className="help">JPEG/PNG/WebP · max 5MB</span></label>
      <div className="grid sm:grid-cols-2 gap-4">
        <CountrySelect required value={v.country} onChange={(c) => setV({ ...v, country: c, phone: v.phone })} testid="profile-country" />
        <PhoneField country={v.country} value={v.phone} onChange={(p) => setV({ ...v, phone: p })} />
        <label className="field">{F[5]}<select value={v.locale} onChange={(e) => setV({ ...v, locale: e.target.value })}>{[["en", "English"], ["ro", "Română"], ["es", "Español"], ["fr", "Français"], ["it", "Italiano"], ["pt", "Português"]].map(([c, n]) => <option key={c} value={c}>{n}</option>)}</select></label>
        <label className="field">{F[6]}<select value={v.timezone} onChange={(e) => setV({ ...v, timezone: e.target.value })}>{Intl.supportedValuesOf("timeZone").map((z) => <option key={z}>{z}</option>)}</select></label>
      </div>
      {!me.terms_accepted_at && <label className="flex gap-2 text-sm"><input type="checkbox" required checked={v.terms} onChange={(e) => setV({ ...v, terms: e.target.checked })} data-testid="profile-terms" /><span>{t("terms")} <Link to={lp("/terms")} className="underline">↗</Link></span></label>}
      {!me.privacy_accepted_at && <label className="flex gap-2 text-sm"><input type="checkbox" required checked={!!v.gdpr} onChange={(e) => setV({ ...v, gdpr: e.target.checked })} data-testid="profile-gdpr" /><span>{x("gdpr")} <Link to={lp("/privacy")} className="underline">↗</Link></span></label>}
      <p className="help">{t("privacyNotice")}</p>
      {error && <p className="error-text" role="alert" data-testid="profile-error">{error}</p>}
      <button className="btn btn-forest justify-self-start" data-testid="profile-save">{tl("controls")[13]}</button>
    </form>
  );
}

function Notifications({ me, reload }) {
  const { t, tl, locale } = useT();
  const api = useApi();
  const [v, setV] = useState({ email: !!me.marketing?.email, phone: !!me.marketing?.whatsapp_sms });
  const save = async () => { await api.put("/me/consents", { email_marketing: v.email, phone_marketing: v.phone, locale }); toast.success(tl("controls")[0]); reload(); };
  return (
    <div className="grid gap-4" data-testid="notifications-tab">
      <label className="flex gap-2"><input type="checkbox" checked={v.email} onChange={(e) => setV({ ...v, email: e.target.checked })} data-testid="optin-email" />{t("emailOptIn")}</label>
      <label className="flex gap-2"><input type="checkbox" checked={v.phone} onChange={(e) => setV({ ...v, phone: e.target.checked })} data-testid="optin-phone" />{t("phoneOptIn")}</label>
      <p className="help">{t("essentialNote")}</p>
      <button className="btn btn-forest justify-self-start" onClick={save} data-testid="consents-save">{tl("controls")[13]}</button>
    </div>
  );
}

function Privacy({ reflections, reload }) {
  const { t, tl } = useT();
  const api = useApi();
  const A = tl("dataActions");
  const exportData = async () => { const d = await api.post("/me/privacy/export-request"); const u = URL.createObjectURL(new Blob([JSON.stringify(d, null, 2)], { type: "application/json" })); const a = document.createElement("a"); a.href = u; a.download = "my-data.json"; a.click(); };
  const delSaved = async () => { for (const r of reflections) await api.del(`/me/reflections/${r.id}`); reload(); };
  const delAcct = async () => { if (window.confirm(t("deleteConfirm"))) { await api.post("/me/privacy/deletion-request"); toast.success(tl("controls")[0]); } };
  return <div className="flex flex-wrap gap-3" data-testid="privacy-tab"><button className="btn btn-ghost btn-sm" onClick={exportData} data-testid="privacy-export">{A[0]}</button><button className="btn btn-ghost btn-sm" onClick={delSaved}>{A[1]}</button><Link to="../notifications" relative="path" className="btn btn-ghost btn-sm">{A[2]}</Link><button className="btn btn-ghost btn-sm !border-[var(--danger)]" onClick={delAcct} data-testid="privacy-delete">{A[3]}</button></div>;
}

function Saved({ list, reload }) {
  const { t, pick } = useT();
  const api = useApi();
  if (!list.length) return <p>{t("emptySaved")}</p>;
  const title = (r) => r.type === "card" ? pick(content.cards.items.find((c) => c.id === r.card_or_result_id)?.content)?.title
    : r.type === "quiz" ? pick(content.quiz.results.find((c) => c.id === r.card_or_result_id)?.content)?.title
      : pick(content.dailyInvitations.items.find((d) => `D${d.dayOfMonth}` === r.card_or_result_id)?.text);
  return list.map((r) => <div key={r.id} className="card p-4 flex justify-between gap-3"><span><span className="eyebrow mr-2">{r.type}</span>{title(r)}</span><button className="text-sm underline" onClick={() => api.del(`/me/reflections/${r.id}`).then(reload)}>×</button></div>);
}

function AccountInner() {
  const { tl, lp } = useT();
  const { tab = "overview" } = useParams();
  const api = useApi();
  const { signOut } = useClerk();
  const [d, setD] = useState(null);
  const load = useCallback(async () => {
    const [me, bookings, orders, club, reflections] = await Promise.all(["/me", "/me/bookings", "/me/orders", "/me/club", "/me/reflections"].map((u) => api.get(u)));
    setD({ me, bookings, orders, club, reflections });
  }, [api]);
  useEffect(() => { load().catch(() => setD({ error: true })); }, [load]);
  const T = tl("tabs");
  if (!d) return <p className="wrap section">…</p>;
  if (d.error) return <p className="wrap section">{tl("errors")[5]}</p>;
  const current = d.me.nickname ? tab : "profile";
  return (
    <>
      <PageHead title={d.me.nickname || tl("utility")[1]} />
      <section className="wrap pb-20 grid md:grid-cols-[220px_1fr] gap-10">
        <nav className="grid gap-1 content-start" aria-label="Account">{TABS.map((k, n) => <Link key={k} to={lp(`/account/${k}`)} aria-current={current === k ? "page" : undefined} className={`px-4 py-2 rounded-xl ${current === k ? "bg-[var(--forest)] text-[var(--ivory)]" : "hover:bg-[var(--paper)]"}`} data-testid={`tab-${k}`}>{T[n]}</Link>)}
          <button onClick={() => signOut({ redirectUrl: lp("") })} className="px-4 py-2 text-left text-sm" data-testid="account-signout">{tl("utility")[5]}</button>
          {["owner", "practitioner", "editor", "support"].includes(d.me.role) && <Link to="/admin" className="px-4 py-2 text-sm underline" data-testid="account-admin">Admin</Link>}</nav>
        <div className="grid gap-4 content-start">
          {current === "overview" && <><ClubTab club={d.club} /><CtaLink to={lp("/book")} className="justify-self-start">{tl("actions")[0]}</CtaLink></>}
          {current === "bookings" && <Bookings list={d.bookings} reload={load} />}
          {current === "orders" && (d.orders.length ? d.orders.map((o) => <div key={o.id} className="card p-4">{o.order_number} · {o.payment_status} · {o.fulfilment_status}{o.tracking ? ` · ${o.tracking}` : ""}</div>) : <p>{tl("emptyOrders")}</p>)}
          {current === "club" && <ClubTab club={d.club} />}
          {current === "saved" && <Saved list={d.reflections} reload={load} />}
          {current === "profile" && <Profile me={d.me} reload={load} />}
          {current === "notifications" && <Notifications me={d.me} reload={load} />}
          {current === "privacy" && <Privacy reflections={d.reflections} reload={load} />}
        </div>
      </section>
    </>
  );
}

export function Account() {
  useSeo(null, "Account");
  return <><Show when="signed-in"><AccountInner /></Show><Show when="signed-out"><Navigate to="/sign-in" replace /></Show></>;
}

export function PaymentResult() {
  const { result } = useParams();
  const [params] = useSearchParams();
  const { t, tl, lp } = useT();
  const api = useApi();
  const [s, setS] = useState(null);
  const P = tl("payment"), B = tl("bookStates");
  useEffect(() => {
    if (result !== "success") return;
    let n = 0, stop = false;
    const poll = async () => {
      try { const r = await api.get(`/payments/status/${params.get("session_id")}`); setS(r); if ((r.state === "confirmed" || r.state === "paid" || r.state === "needs_resolution") || ++n > 15) return; } catch { setS({ error: true }); return; }
      if (!stop) setTimeout(poll, 2000);
    };
    poll(); return () => { stop = true; };
  }, [result, params, api]);
  const msg = result !== "success" ? P[1] : !s ? P[0] : s.error ? B[5] : (s.state === "confirmed" || s.state === "paid") ? B[3] : s.state === "needs_resolution" ? B[5] : B[4];
  return <section className="wrap section max-w-[720px]"><h1 className="text-5xl" role="status" data-testid="payment-status">{msg}</h1><CtaLink to={lp(s?.kind === "order" ? "/account/orders" : "/account/bookings")} className="mt-8">{s?.kind === "order" ? P[3] : P[2]}</CtaLink></section>;
}

export function SessionRoom() {
  const { id } = useParams();
  const { tl } = useT();
  const api = useApi();
  const R = tl("room");
  const [timer, setTimer] = useState(null);
  const [msg, setMsg] = useState("");
  const [dev, setDev] = useState("");
  useEffect(() => { const f = () => api.get(`/bookings/${id}/timer`).then(setTimer).catch((e) => setMsg(errMsg(e, ""))); f(); const iv = setInterval(f, 15000); return () => clearInterval(iv); }, [id, api]);
  const join = async () => { try { await api.post(`/bookings/${id}/join-token`); } catch (e) { setMsg(errMsg(e, tl("errors")[5])); } };
  const test = async () => { try { const s = await navigator.mediaDevices.getUserMedia({ audio: true, video: true }); s.getTracks().forEach((t) => t.stop()); setDev("✓"); } catch { setDev("Camera/microphone not available in this browser."); } };
  return (
    <section className="wrap section max-w-[720px] grid gap-4" data-testid="session-room">
      <h1 className="text-5xl">{R[0]}</h1><p>{R[1]}</p>
      <button className="btn btn-ghost justify-self-start" onClick={test} data-testid="device-test">{R[2]}</button><p aria-live="polite">{dev}</p>
      {timer && <p data-testid="session-timer">{R[3]}: {Math.floor(timer.remaining_seconds / 60)} min · {R[5]}</p>}
      <p className="help">{R[4]}</p>
      <button className="btn btn-gold justify-self-start" onClick={join} data-testid="join-session">{tl("bookStates")[7]}</button>
      {msg && <p className="notice" role="alert" data-testid="session-msg">{msg}</p>}
    </section>
  );
}

export { API };
